/**
 * Pure navigation logic for sidebar visibility.
 * No React dependencies - fully testable outside the browser.
 *
 * DashboardLayout maps iconName strings to actual Lucide components at render time.
 *
 * Two-tier RBAC: GLOBAL_NAV is always visible to any authenticated user (no
 * project axis applies). Every project-scoped item carries the `action` it
 * gates, so the sidebar can decide visibility with can(action) against the
 * caller's role on the ACTIVE project — value-for-value with backend rbac.py.
 * Each item's `action` mirrors the GET/visibility gate of that page's backend
 * route (all READ, except Import/Export which gates the whole page on
 * IMPORT_EXPORT).
 */

import { Action } from './auth';

// ─── Nav item arrays ────────────────────────────────────────────────

export const GLOBAL_NAV = [
  { to: '/dashboard', label: 'Dashboard', accessKey: 'dashboard', iconName: 'LayoutDashboard' },
  { to: '/projects', label: 'Projects', accessKey: 'projects', iconName: 'Building2' },
  { to: '/ai-assistant', label: 'AI Assistant', accessKey: 'ai-assistant', iconName: 'Sparkles' },
];

export const SCHEDULE_NAV = [
  { to: '/wbs', label: 'WBS', accessKey: 'wbs', action: Action.READ, iconType: 'image', iconKey: 'wbs' },
  { to: '/scheduling', label: 'Activities', accessKey: 'activities', action: Action.READ, iconType: 'image', iconKey: 'activity' },
  { to: '/calendars', label: 'Calendars', accessKey: 'schedules', action: Action.READ, iconName: 'CalendarDays' },
  { to: '/resources', label: 'Resources', accessKey: 'schedules', action: Action.READ, iconName: 'Package' },
  { to: '/baselines', label: 'Baselines', accessKey: 'baselines', action: Action.READ, iconName: 'GitCompare' },
  { to: '/history', label: 'History', accessKey: 'history', action: Action.READ, iconName: 'History' },
];

export const ANALYSIS_NAV = [
  { to: '/scenarios', label: 'Scenarios', accessKey: 'scenarios', action: Action.READ, iconName: 'GanttChart' },
  { to: '/evm', label: 'EVM', accessKey: 'evm', action: Action.READ, iconName: 'ClipboardList' },
];

export const OPERATIONS_NAV = [
  { to: '/field-updates', label: 'Schedule Updates', accessKey: 'field-updates', action: Action.READ, iconName: 'FolderKanban' },
];

export const SETTINGS_NAV = [
  { to: '/notifications', label: 'Notifications', accessKey: 'notifications', action: Action.READ, iconName: 'Bell' },
  { to: '/email-notifications', label: 'Email Settings', accessKey: 'notifications', action: Action.READ, iconName: 'Mail' },
  { to: '/import-export', label: 'Import / Export', accessKey: 'import-export', action: Action.IMPORT_EXPORT, iconName: 'Upload' },
];

// ─── Path classification ────────────────────────────────────────────

/** Paths that require an active project to be meaningful */
const PROJECT_REQUIRED_PATHS = new Set([
  '/scheduling', '/calendars', '/resources', '/baselines', '/history',
  '/field-updates', '/notifications', '/email-notifications', '/import-export',
  '/scenarios', '/evm', '/gantt', '/wbs', '/activities',
]);

/**
 * Check if a given path requires an active project.
 * @param {string} path
 * @returns {boolean}
 */
export function isProjectRequiredForPath(path) {
  return PROJECT_REQUIRED_PATHS.has(path);
}

// ─── Filtering helpers ──────────────────────────────────────────────

/**
 * Filter nav items by role access.
 * @param {Array} items - Nav item objects with accessKey
 * @param {function} canAccessPage - (accessKey) => boolean
 * @returns {Array} Items the user can access
 */
export function filterByAccess(items, canAccessPage) {
  return items.filter(item => canAccessPage(item.accessKey));
}

/**
 * Get global nav items visible for a given access function.
 * These are always shown regardless of active project.
 * @param {function} canAccessPage - (accessKey) => boolean
 * @returns {Array}
 */
export function getGlobalNavItems(canAccessPage) {
  return filterByAccess(GLOBAL_NAV, canAccessPage);
}

/**
 * Get project-specific nav sections, filtered by access and with empty sections removed.
 * @param {function} canAccessPage - (accessKey) => boolean
 * @returns {Array<{id: string, title: string, items: Array}>}
 */
export function getProjectNavSections(canAccessPage) {
  return [
    { id: 'schedule', title: 'Schedule', items: filterByAccess(SCHEDULE_NAV, canAccessPage) },
    { id: 'analysis', title: 'Analysis', items: filterByAccess(ANALYSIS_NAV, canAccessPage) },
    { id: 'operations', title: 'Operations', items: filterByAccess(OPERATIONS_NAV, canAccessPage) },
    { id: 'settings', title: 'Settings', items: filterByAccess(SETTINGS_NAV, canAccessPage) },
  ].filter(section => section.items.length > 0);
}
